
import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, RotateCcw, CheckSquare, Square, ChevronDown, ChevronUp, Share2, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppUser } from '../types';
import { PackingItem, PackingCategory, getDefaultCategories, DEFAULT_CATEGORIES } from './mycontent/packingTypes';

// ─── LocalStorage helpers ─────────────────────────────────────────────────────

const LS_KEY = 'fricamp_packing_v2';
// Tracks which auto-created (default) items the user has deleted, so they stay
// deleted across reloads. Stored as `"${catId}:${itemId}"` keys.
const LS_KEY_REMOVED = 'fricamp_packing_v2_removed';

function loadRemovedDefaults(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_KEY_REMOVED);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function loadList(t: (key: string) => string): PackingCategory[] {
  const defaults = getDefaultCategories(t);
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return defaults;
    const saved: PackingCategory[] = JSON.parse(raw);
    const removed = loadRemovedDefaults();
    // Merge: keep default structure, restore checked states + custom items.
    // Default items the user has deleted are filtered out and not re-added.
    return defaults.map(cat => {
      const savedCat = saved.find(c => c.id === cat.id);
      const mergedItems: PackingItem[] = cat.items
        .filter(item => !removed.has(`${cat.id}:${item.id}`))
        .map(item => {
          const savedItem = savedCat?.items.find(i => i.id === item.id);
          return savedItem ? { ...item, checked: savedItem.checked } : item;
        });
      // Add custom items
      const customs = savedCat?.items.filter(i => i.custom) ?? [];
      return { ...cat, items: [...mergedItems, ...customs] };
    });
  } catch {
    return defaults;
  }
}

function saveList(cats: PackingCategory[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(cats));
    // Recompute which default items are now missing → remember them as deleted.
    const removed: string[] = [];
    for (const defCat of DEFAULT_CATEGORIES) {
      const curCat = cats.find(c => c.id === defCat.id);
      for (const defItem of defCat.items) {
        if (!curCat || !curCat.items.some(i => i.id === defItem.id)) {
          removed.push(`${defCat.id}:${defItem.id}`);
        }
      }
    }
    localStorage.setItem(LS_KEY_REMOVED, JSON.stringify(removed));
  } catch {}
}

// ─── Component ────────────────────────────────────────────────────────────────

interface PackingListModalProps {
  onClose: () => void;
  currentUser?: AppUser | null;
}

const PackingListModal: React.FC<PackingListModalProps> = ({ onClose, currentUser }) => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<PackingCategory[]>(() => loadList(t));
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [addingIn, setAddingIn] = useState<string | null>(null);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [copied, setCopied] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareText, setShareText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Persist on every change
  useEffect(() => { saveList(categories); }, [categories]);

  // Focus input when opening add-row
  useEffect(() => {
    if (addingIn) setTimeout(() => inputRef.current?.focus(), 50);
  }, [addingIn]);

  const totalItems   = categories.reduce((a, c) => a + c.items.length, 0);
  const checkedItems = categories.reduce((a, c) => a + c.items.filter(i => i.checked).length, 0);
  const progress     = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

  const toggleItem = (catId: string, itemId: string) => {
    setCategories(prev => prev.map(cat =>
      cat.id !== catId ? cat : {
        ...cat,
        items: cat.items.map(item =>
          item.id !== itemId ? item : { ...item, checked: !item.checked }
        ),
      }
    ));
  };

  const deleteCustomItem = (catId: string, itemId: string) => {
    setCategories(prev => prev.map(cat =>
      cat.id !== catId ? cat : {
        ...cat,
        items: cat.items.filter(item => item.id !== itemId),
      }
    ));
  };

  const addCustomItem = (catId: string) => {
    const label = newItemLabel.trim();
    if (!label) { setAddingIn(null); return; }
    const id = `custom_${Date.now()}`;
    setCategories(prev => prev.map(cat =>
      cat.id !== catId ? cat : {
        ...cat,
        items: [...cat.items, { id, label, checked: false, custom: true }],
      }
    ));
    setNewItemLabel('');
    setAddingIn(null);
  };

  const buildTextList = () => {
    const lines: string[] = [`🎒 ${t('myContent.tabs.packing').toUpperCase()}\n`];
    for (const cat of categories) {
      lines.push(`${cat.emoji} ${cat.label}`);
      for (const item of cat.items) {
        lines.push(`${item.checked ? '✅' : '☐'} ${item.label}`);
      }
      lines.push('');
    }
    return lines.join('\n');
  };

  const shareList = async () => {
    const text = buildTextList();
    if (navigator.share) {
      try { await navigator.share({ title: t('packing.new_list'), text }); return; } catch {}
    }
    // Fallback: show copy dialog
    setShareText(text);
    setShowShareDialog(true);
  };

  const copyShareText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const printList = () => {
    const rows = categories.map(cat => {
      const items = cat.items.map(item =>
        `<li style="padding:3px 0;${item.checked ? 'opacity:.45;text-decoration:line-through' : ''}">${item.checked ? '☑' : '☐'} ${item.label}</li>`
      ).join('');
      return `<h3 style="margin:16px 0 4px;font-size:13px">${cat.emoji} ${cat.label}</h3><ul style="list-style:none;padding:0;margin:0">${items}</ul>`;
    }).join('');

    const userName = currentUser?.displayName || currentUser?.email || '';
    const userPhoto = currentUser?.photoURL || '';
    const userBlock = userName
      ? `<div style="text-align:right">
          ${userPhoto ? `<img src="${userPhoto}" style="width:40px;height:40px;border-radius:50%;vertical-align:middle;margin-right:8px;object-fit:cover" />` : ''}
          <span style="font-size:13px;color:#555;vertical-align:middle">${userName}</span>
         </div>`
      : '';

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${t('packing.new_list')}</title>
      <style>
        body{font-family:sans-serif;padding:24px;max-width:680px;margin:0 auto;color:#111}
        .header{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:16px}
        .brand{display:flex;align-items:center;gap:10px}
        .brand-logo{width:40px;height:40px;border-radius:50%;object-fit:cover}
        .brand-name{font-weight:900;font-size:18px;letter-spacing:-0.5px}
        .progress{font-size:11px;color:#666;margin:0 0 16px}
        h3{margin:16px 0 4px;font-size:13px}
        ul{list-style:none;padding:0;margin:0}
        li{padding:3px 0;font-size:12px}
        @media print{body{padding:12px}}
      </style>
      </head><body>
      <div class="header">
        <div class="brand">
          <img class="brand-logo" src="/Fricamplogo.png" />
          <span class="brand-name">Fricamp</span>
        </div>
        ${userBlock}
      </div>
      <p class="progress">🎒 ${t('packing.new_list')} &nbsp;·&nbsp; ${checkedItems} / ${totalItems} &nbsp;·&nbsp; ${progress}%</p>
      ${rows}
      </body></html>`);
    win.document.close();
    win.print();
  };

  const resetAll = () => {
    setCategories(prev => prev.map(cat => ({
      ...cat,
      items: cat.items.map(item => ({ ...item, checked: false })),
    })));
  };

  const toggleCollapse = (catId: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 w-full sm:max-w-lg bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[90dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/8 shrink-0">
          <span className="text-2xl">🎒</span>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-black uppercase tracking-widest text-[var(--text-primary)]">{t('packing.new_list')}</h2>
            <p className="text-tiny text-[var(--text-secondary)] font-medium mt-0.5">
              {checkedItems} / {totalItems} · {progress}%
            </p>
          </div>
          <button
            onClick={resetAll}
            className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition-all"
            title={t('packing.reset_all')}
          >
            <RotateCcw size={14} />
          </button>
          <button
            onClick={shareList}
            className={`p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-all ${copied ? 'text-emerald-400 bg-emerald-500/10' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]'}`}
            title={t('packing.share_list')}
          >
            <Share2 size={14} />
          </button>
          <button
            onClick={printList}
            className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition-all"
            title={t('common.print', 'Print')}
          >
            <Printer size={14} />
          </button>
          <button
            onClick={onClose}
            className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition-all"
          >
            <X size={16} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-[var(--bg-surface-alt)] shrink-0">
          <div
            className="h-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-2" style={{ overscrollBehavior: 'contain' }}>
          {categories.map(cat => {
            const catChecked  = cat.items.filter(i => i.checked).length;
            const isCollapsed = collapsed.has(cat.id);
            const isAdding    = addingIn === cat.id;

            return (
              <div key={cat.id} className="border border-white/8 rounded-xl overflow-hidden">
                {/* Category header */}
                <button
                  onClick={() => toggleCollapse(cat.id)}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 bg-white/3 hover:bg-[var(--bg-surface-alt)] transition-colors text-left"
                >
                  <span className="text-base leading-none">{cat.emoji}</span>
                  <span className="flex-1 text-xs font-black uppercase tracking-wider text-[var(--text-primary)]">
                    {cat.label}
                  </span>
                  <span className={`text-tiny font-black px-2 py-0.5 rounded-full ${
                    catChecked === cat.items.length && cat.items.length > 0
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)]'
                  }`}>
                    {catChecked}/{cat.items.length}
                  </span>
                  {isCollapsed
                    ? <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" />
                    : <ChevronUp   size={14} className="text-[var(--text-muted)] shrink-0" />
                  }
                </button>

                {/* Items */}
                {!isCollapsed && (
                  <div className="divide-y divide-[var(--border-primary)]">
                    {cat.items.map(item => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/3 transition-colors group"
                      >
                        <button
                          onClick={() => toggleItem(cat.id, item.id)}
                          className={`shrink-0 transition-colors ${item.checked ? 'text-emerald-500' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                        >
                          {item.checked
                            ? <CheckSquare size={16} fill="currentColor" />
                            : <Square size={16} />
                          }
                        </button>
                        <span
                          className={`flex-1 text-xs font-medium transition-colors cursor-pointer select-none ${
                            item.checked ? 'line-through text-[var(--text-muted)]' : 'text-[var(--text-primary)]'
                          }`}
                          onClick={() => toggleItem(cat.id, item.id)}
                        >
                          {item.label}
                          {item.custom && (
                            <span className="ml-1.5 text-3xs font-black uppercase tracking-wider text-[var(--text-muted)]">
                              {t('myContent.filter_custom')}
                            </span>
                          )}
                        </span>
                        <button
                          onClick={() => deleteCustomItem(cat.id, item.id)}
                          className="sm:opacity-0 sm:group-hover:opacity-100 p-2 rounded text-[var(--text-muted)] hover:text-red-400 transition-all min-w-[44px] min-h-[44px] flex items-center justify-center"
                          title={t('packing.delete_item')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}

                    {/* Add custom item row */}
                    {isAdding ? (
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-white/3">
                        <input
                          ref={inputRef}
                          value={newItemLabel}
                          onChange={e => setNewItemLabel(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') addCustomItem(cat.id);
                            if (e.key === 'Escape') { setAddingIn(null); setNewItemLabel(''); }
                          }}
                          placeholder={t('packing.item_name_placeholder')}
                          className="flex-1 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-lg px-3 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-emerald-500/50"
                        />
                        <button
                          onClick={() => addCustomItem(cat.id)}
                          className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-black hover:bg-emerald-500/30 transition-colors"
                        >
                          {t('packing.add_item')}
                        </button>
                        <button
                          onClick={() => { setAddingIn(null); setNewItemLabel(''); }}
                          className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setAddingIn(cat.id); setNewItemLabel(''); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-tiny font-medium text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-white/3 transition-colors"
                      >
                        <Plus size={12} /> {t('packing.add_custom')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/8 shrink-0">
          <p className="text-2xs text-[var(--text-muted)] text-center">
            {t('packing.auto_saved', 'List is saved automatically in your browser')}
          </p>
        </div>
      </div>

      {/* Share dialog */}
      {showShareDialog && (
        <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowShareDialog(false)} />
          <div className="relative z-10 w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl shadow-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-black uppercase tracking-widest text-[var(--text-secondary)]">{t('packing.share_list')}</p>
              <button onClick={() => setShowShareDialog(false)} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"><X size={14} /></button>
            </div>
            <textarea
              readOnly
              value={shareText}
              rows={10}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2.5 text-tiny text-[var(--text-secondary)] font-mono resize-none focus:outline-none"
            />
            <button
              onClick={copyShareText}
              className={`w-full py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${copied ? 'bg-emerald-600 text-white' : 'bg-[var(--bg-surface)] text-[var(--text-primary)] hover:bg-white/15'}`}
            >
              {copied ? `✓ ${t('common.copied', 'Copied!')}` : t('common.copy_text', 'Copy text')}
            </button>
            <p className="text-3xs text-[var(--text-muted)] text-center">{t('packing.share_hint', 'Paste in SMS, email or chat to share the list')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackingListModal;
